import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { loginRequest } from "../authConfig";
import { msalInstance as appMsalInstance } from "../index";
import { hasValidAppToken } from "./appToken";
import { deleteAccessTokenCookie, setAccessTokenCookie } from "./cookies";
import { authDebug, authDebugError } from "./safeAuthLog";

// Use the already initialized MSAL instance from index.js
const getMsalInstance = () => {
  return appMsalInstance;
};

/**
 * Token refresh utility using MSAL
 * Let MSAL handle refresh tokens - we just ensure we can get a valid token
 * @deprecated This function is kept for backward compatibility but should not be used.
 * MSAL handles token refresh automatically via acquireTokenSilent.
 */
export const refreshTokenIfNeeded = async (): Promise<boolean> => {
  try {
    authDebug("refreshTokenIfNeeded: attempting to acquire a fresh token");

    // Get the initialized MSAL instance
    const instance = getMsalInstance();

    // First, handle any pending redirect promise (user might be returning from login)
    try {
      const redirectResponse = await instance.handleRedirectPromise();
      if (redirectResponse?.account) {
        instance.setActiveAccount(redirectResponse.account);
        if (redirectResponse.accessToken) {
          setAccessTokenCookie(redirectResponse.accessToken);
          return true;
        }
      }
    } catch (redirectError) {
      // Ignore redirect errors, continue with normal flow
      authDebugError(
        "refreshTokenIfNeeded: no redirect (or redirect error)",
        redirectError
      );
    }

    // Make sure the instance is initialized before using it
    if (!instance.getActiveAccount()) {
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        authDebug("refreshTokenIfNeeded: setting active account from cache");
        instance.setActiveAccount(accounts[0]);
      } else {
        authDebug("refreshTokenIfNeeded: no accounts found");
        // Don't trigger logout here - just return false gracefully
        // The calling code should handle this appropriately
        return false;
      }
    }

    const account = instance.getActiveAccount();
    if (!account) {
      authDebug("refreshTokenIfNeeded: no active account");
      // Don't trigger logout here - just return false gracefully
      return false;
    }

    // Try to silently acquire a new token - MSAL will use refresh token if needed
    try {
      authDebug("refreshTokenIfNeeded: acquiring token silently via MSAL");
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
        scopes: loginRequest.scopes.filter(
          (scope): scope is string => scope !== undefined
        ),
      });

      if (response?.accessToken) {
        // Save the token to cookies so apiClient can use it
        setAccessTokenCookie(response.accessToken);
        return true;
      } else {
        authDebug("refreshTokenIfNeeded: no access token in response");
        return false;
      }
    } catch (refreshError) {
      authDebugError(
        "refreshTokenIfNeeded: token silent refresh failed",
        refreshError
      );

      // Check if this is an interaction required error
      if (refreshError instanceof InteractionRequiredAuthError) {
        authDebug("refreshTokenIfNeeded: interaction required");
        return false;
      }

      return false;
    }
  } catch (error) {
    authDebugError("refreshTokenIfNeeded: unexpected failure", error);
    return false;
  }
};

/**
 * Check if user has a valid authentication state
 * Uses MSAL account state instead of cookies
 */
export const isUserAuthenticated = (): boolean => {
  // External (email-code) users have no MSAL account — a valid app-issued token
  // is their session, good for its own ~8h lifetime.
  if (hasValidAppToken()) {
    return true;
  }
  const instance = getMsalInstance();
  const account = instance.getActiveAccount();
  if (!account) {
    const accounts = instance.getAllAccounts();
    return accounts.length > 0;
  }
  return true;
};

/**
 * Enhanced logout function that handles authentication failures
 * @param redirect - Whether to redirect after logout (defaults to false)
 * @param reason - Reason for logout (for logging purposes)
 * @returns {Promise<void>}
 */
export const logoutUser = async (
  redirect = false,
  reason = "User initiated"
): Promise<void> => {
  try {
    authDebug(`logoutUser: logging out (${reason})`);

    // Clear MSAL state
    const instance = getMsalInstance();
    // External (email-code) users have no Microsoft session to end — decide
    // before we clear the token so we never send them to the Microsoft logout.
    const isExternal = hasValidAppToken();
    if (instance) {
      // Reset active account (MSAL doesn't have a direct removeAccount method)
      instance.setActiveAccount(null);

      // Only the MSAL (internal) path uses logoutRedirect.
      if (redirect && !isExternal) {
        authDebug("logoutUser: redirecting to logout page");
        try {
          await instance.logoutRedirect({
            onRedirectNavigate: () => false,
            postLogoutRedirectUri: window.location.origin + "/",
          });
        } catch (logoutError) {
          authDebugError(
            "logoutUser: MSAL logout redirect failed; forcing redirect",
            logoutError
          );
          window.location.href = "/";
        }
      }
    }

    // Clear in-memory access token (and any legacy token cookies)
    deleteAccessTokenCookie();

    // Clear any additional auth-related storage
    try {
      // Clear session storage
      sessionStorage.clear();

      // Clear auth-related localStorage items
      const localStorageKeys = Object.keys(localStorage);
      localStorageKeys.forEach((key) => {
        if (
          key.startsWith("msal.") ||
          key.includes("login") ||
          key.includes("auth") ||
          key.includes("token")
        ) {
          localStorage.removeItem(key);
        }
      });
    } catch (storageError) {
      authDebugError("logoutUser: error clearing storage", storageError);
    }

    // External users skip MSAL's redirect above, so send them to landing here.
    if (redirect && isExternal) {
      window.location.href = "/";
    }

    authDebug("logoutUser: completed");
  } catch (error) {
    authDebugError("logoutUser: failed", error);

    // Force redirect if explicitly requested and MSAL logout failed
    if (redirect) {
      authDebug("logoutUser: forcing redirect due to failure");
      window.location.href = "/";
    }
  }
};

/**
 * Handle authentication errors globally
 * This function should be called when authentication fails
 * @param error - The authentication error
 * @param context - Context where the error occurred (for logging)
 */
export const handleAuthenticationError = async (
  error: any,
  context = "Unknown"
): Promise<void> => {
  authDebugError(`handleAuthenticationError (${context})`, error);

  // Check if this is a token expiration error
  const isTokenExpired =
    error?.message?.includes("401") ||
    error?.message?.includes("expired") ||
    error?.message?.includes("Authentication failed");

  if (isTokenExpired) {
    authDebug("handleAuthenticationError: token expired; logging out");
    // Since callApi now handles 401s and token refresh via MSAL,
    // if we get here, the user likely needs to log in again
    await logoutUser(true, `Authentication error in ${context}`);
  } else {
    authDebug("handleAuthenticationError: auth error; logging out");
    await logoutUser(true, `Authentication error in ${context}`);
  }
};

/**
 * Setup automatic token refresh with enhanced error handling
 * @param intervalMinutes - How often to check for token refresh (default: 15 minutes)
 * @returns {number} - Interval ID for cleanup
 * @deprecated MSAL handles token refresh automatically. This is kept for backward compatibility.
 */
export const setupTokenRefresh = (intervalMinutes = 15) => {
  authDebug("setupTokenRefresh: MSAL manages refresh; no-op");

  // MSAL handles token refresh automatically via acquireTokenSilent
  // We don't need to set up periodic checks anymore
  // Return a dummy interval ID for cleanup compatibility
  return 0;
};

/**
 * Cleanup automatic token refresh
 */
export const cleanupTokenRefresh = () => {
  const intervalId = (window as any).__tokenRefreshIntervalId;
  if (intervalId) {
    authDebug("cleanupTokenRefresh: clearing interval");
    clearInterval(intervalId);
    delete (window as any).__tokenRefreshIntervalId;
  }
};

/**
 * Global error handler for authentication failures
 * This should be called from error boundaries or global error handlers
 *
 * NOTE: We no longer intercept fetch calls globally as callApi handles 401s.
 * This prevents conflicts and lets MSAL manage tokens properly.
 */
export const setupGlobalAuthErrorHandler = () => {
  // Handle unhandled promise rejections related to authentication
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;

    // Check if this is an authentication-related error
    if (
      error?.message?.includes("401") ||
      error?.message?.includes("Authentication failed") ||
      error?.message?.includes("expired")
    ) {
      authDebugError("setupGlobalAuthErrorHandler: unhandled auth error", error);

      // Prevent the default error handling
      event.preventDefault();

      // Handle the authentication error
      handleAuthenticationError(error, "Global Error Handler");
    }
  });

  authDebug("setupGlobalAuthErrorHandler: installed");
};
